import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';
import { Button } from '../components/Button';
import { H2 } from '../components/H2';
import { Checkbox } from '../components/Checkbox';
import { TextField } from '../components/TextField';
import { LoginRequest } from '../types/types';
import { userLogin, getUserInfo } from '../apis/user';
import { Action, Bridge } from '../plugins/bridge';
import { AIRBRIDGE_EVENT } from '../constants/airbridge';
import { useUserDispatch } from '../context/UserContext';
import { BRAZE_EVENT } from '../constants/braze';
import { nToast } from '../plugins/reactToast';
import qs from 'qs';
import { ButtonEventProperties } from '../types/amplitudeEventProperties';
import { useCookies } from 'react-cookie';
import moment from 'moment';
import { TargetingGates } from '../plugins/targetingGates';

function onClickNonMember() {
  Bridge.sendAmplitude('non_member_order_check_button_clicked');
}

export const LoginPage = () => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginRequest>();
  const history = useHistory();
  const dispatch = useUserDispatch();
  const query = qs.parse(history.location.search, { ignoreQueryPrefix: true });
  const [isSaveId, setIsSaveId] = useState(false);
  const [cookies, setCookie, removeCookie] = useCookies(['userId_carhartt']);

  useEffect(() => {
    if (cookies.userId_carhartt) {
      setValue('email', cookies.userId_carhartt);
      setIsSaveId(true);
    }
  }, []);

  useEffect(() => {
    Bridge.sendAmplitude('log_in_page_viewed');
  }, []);

  const signUpButtonClick = () => {
    const signUpClickEventProperties: ButtonEventProperties = {
      button_page: 'sign_up page',
    };

    Bridge.sendAmplitude('sign_up_button_clicked', signUpClickEventProperties);
  };

  const usersetting = async () => {
    await getUserInfo().then(r => {
      const option = {
        userAttribute: {
          userID: r.data.payload.userId.toString(),
          userEmail: r.data.payload.userName.toString(),
          userPhone: r.data.payload.userPhone.toString(),
          gender: r.data.payload.userGender?.toString(),
        },
      };

      Bridge.sendAirbridge(AIRBRIDGE_EVENT.signIn, undefined, option);
      Bridge.sendBraze('changeUser', String(r.data.payload.userId.toString()));
      Bridge.sendAmplitude(
        'setUserId',
        String(r.data.payload.userId.toString()),
      );
      Bridge.sendAirbridge(
        'setUserId',
        undefined,
        String(r.data.payload.userId.toString()),
      );
      Bridge.sendAmplitude(
        'setUserProperties#first_name',
        r.data.payload.userName.toString(),
      );
      Bridge.sendAmplitude(
        'setUserProperties#gender',
        r.data.payload.userGender,
      );
      Bridge.sendAmplitude('setUserProperties#phone', r.data.payload.userPhone);
    });
  };

  const setUserInfo = () => {
    getUserInfo()
      .then(r =>
        dispatch({
          type: 'LOGIN',
          payload: r.data.payload,
        }),
      )
      .catch(e => console.log(e));
  };

  useEffect(() => {
    localStorage.removeItem('customerName');
    localStorage.removeItem('customerPhone');
  }, []);

  const handleLogin = (data: LoginRequest) => {
    userLogin(data)
      .then(async ({ data: { code }, headers }) => {
        if (code === 'SUCCESS') {
          if (isSaveId) {
            setCookie('userId_carhartt', data.email, {
              path: '/',
              expires: moment().add(30, 'days').toDate(),
            });
          } else {
            removeCookie('userId_carhartt');
          }

          localStorage.setItem('authToken', headers.authorization);
          Bridge.postMessage(Action.setToken, headers.authorization);
          await setUserInfo();
          await usersetting();
          Bridge.sendBraze(BRAZE_EVENT.SIGN_IN);
          TargetingGates.sendSignIn({ email: data.email });
          Bridge.sendAmplitude('setUserProperties#email', data.email);
          Bridge.sendAmplitude('sign_in_completed');
          // 비회원 정보 있으면 삭제
          localStorage.removeItem('customerName');
          localStorage.removeItem('customerPhone');

          if (!!query.location) {
            history.push(query.location as string);
          } else {
            history.push('/?backPage=/login');
          }
          // window.location.reload();
        }
      })
      .catch(e => {
        console.log(e);
        if (e.response?.status === 403)
          nToast(
            '아이디 또는 비밀번호를 잘못 입력하셨습니다. 다시 시도해 주세요.',
          );
        console.log('로그인 실패');
      });
  };

  return (
    <>
      <div className="max-w-md mx-auto space-y-6 pt-8 md:pt-14">
        <H2 className="text-center">로그인</H2>

        <form
          className="flex flex-col space-y-4"
          onSubmit={handleSubmit(handleLogin)}>
          <TextField
            label="아이디"
            type="email"
            placeholder="이메일을 입력해주세요."
            helper={errors.email?.message}
            {...register('email', { required: '이메일을 입력해주세요.' })}
          />

          <TextField
            label="비밀번호"
            type="password"
            helper={errors.password?.message}
            placeholder="비밀번호를 입력해주세요."
            {...register('password', { required: '비밀번호를 입력해주세요' })}
          />
          <div className="items-center space-x-2 text-13 font-normal">
            <div className="flex flex-row float-left">
              <Checkbox
                className="mr-1"
                onChange={e => setIsSaveId(e.target.checked)}
                checked={isSaveId}
              />{' '}
              아이디 저장
            </div>
            <div className="flex float-right items-center space-x-2">
              <Link to="/find/id">{'아이디 찾기'}</Link>
              <div className="w-px h-3 bg-gray-400" />
              <Link to="/find/password">{'비밀번호 찾기'}</Link>
            </div>
          </div>

          <div className="space-y-4 flex flex-col">
            <Button text="로그인" className="filled-brand-1" />
            <Button
              text="회원가입"
              className="outlined-brand-1 font-normal"
              onClick={() => {
                signUpButtonClick();
                history.push('/personal-verification');
              }}
            />
          </div>

          <Link
            to="/non-member"
            className="text-gray-500 text-15 mx-auto "
            onClick={() => {
              onClickNonMember();
            }}>
            비회원 주문조회
          </Link>
        </form>
      </div>
    </>
  );
};
